/**
 * Decisions (Phase 5.2): saving a message as a decision, and the lists in the
 * room panel and on the project page. Lists reload when any decision changes.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { api, ApiError } from "../lib/api";
import { useStore, type Message } from "../lib/store";
import { ErrorNote, Modal } from "./ui";

interface DecisionView {
  id: string;
  text: string;
  room: { id: string; name: string } | null;
  messageId: number | null;
  savedByName: string | null;
  createdAt: string;
  canChange: boolean;
}

const day = (iso: string) => new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });

export function SaveDecisionModal({ message, projectName, onClose }: { message: Message; projectName: string | null; onClose: () => void }) {
  // Quoted lines are what someone else said; the decision is the reply itself.
  const [text, setText] = useState(() => (message.body.split("\n").filter((l) => !/^>( |$)/.test(l)).join("\n").trim() || message.body).slice(0, 2000));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal title="Save as decision" onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await api(`/api/messages/${message.id}/decision`, { text });
            onClose();
          } catch (err) {
            setError(err instanceof ApiError ? err.message : "Could not save.");
            setBusy(false);
          }
        }}
      >
        <ErrorNote error={error} />
        <label className="field">
          <span>The decision, in a sentence or two</span>
          <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} required autoFocus />
          <small>
            {projectName
              ? `Shared with every room of the ${projectName} project. Agents there read it with every question.`
              : "Kept with this room. Agents here read it with every question."}
          </small>
        </label>
        <div className="actions">
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="submit" disabled={busy || !text.trim()}>
            {busy ? "Saving" : "Save decision"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** A list of decisions from one endpoint (a room's or a project's). */
export function DecisionList({ url, hereRoomId, empty }: { url: string; hereRoomId?: string; empty: string }) {
  const version = useStore((s) => s.decisionsVersion);
  const [list, setList] = useState<DecisionView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<DecisionView | null>(null);
  useEffect(() => {
    api<{ decisions: DecisionView[] }>(url).then(
      (r) => setList(r.decisions),
      (err) => setError(err instanceof ApiError ? err.message : "Could not load the decisions."),
    );
  }, [url, version]);

  if (error) return <ErrorNote error={error} />;
  if (!list) return <p className="muted-text small">Loading</p>;
  if (!list.length) return <p className="muted-text small">{empty}</p>;
  return (
    <>
      <ul className="decisions">
        {list.map((d) => (
          <li key={d.id}>
            <p>{d.text}</p>
            <small>
              {day(d.createdAt)} · {d.savedByName ?? "Former member"}
              {d.room && d.room.id !== hereRoomId && (
                <>
                  {" "}
                  · <Link to={`/r/${d.room.id}`}>#{d.room.name}</Link>
                </>
              )}
              {d.canChange && (
                <>
                  {" "}
                  ·{" "}
                  <button className="linkish" type="button" onClick={() => setEditing(d)}>
                    Edit
                  </button>{" "}
                  ·{" "}
                  <button
                    className="linkish"
                    type="button"
                    onClick={() => confirm("Remove this decision? Agents stop reading it.") && void api(`/api/decisions/${d.id}`, undefined, "DELETE")}
                  >
                    Remove
                  </button>
                </>
              )}
            </small>
          </li>
        ))}
      </ul>
      {editing && <EditDecision decision={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function EditDecision({ decision, onClose }: { decision: DecisionView; onClose: () => void }) {
  const [text, setText] = useState(decision.text);
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal title="Edit decision" onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await api(`/api/decisions/${decision.id}`, { text }, "PATCH");
            onClose();
          } catch (err) {
            setError(err instanceof ApiError ? err.message : "Could not save.");
          }
        }}
      >
        <ErrorNote error={error} />
        <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} required autoFocus aria-label="Decision" />
        <div className="actions">
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="submit" disabled={!text.trim()}>
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}
